import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/common/SEOHead';

const webProjects = [
  {
    name: 'BokaNära',
    category: 'Boknings- och offertplattform',
    url: 'https://bokanara.se',
    image: '/project-screenshots/bokanara-home.webp',
    description: 'En webbplattform där serviceföretag kan presentera tjänster, ta emot offertförfrågningar och hantera bokningsflöden.',
    result: 'Visar arbete med kundflöden, företagspanel, prisberäkning, formulär, lokal synlighet och administration.',
    features: ['Företagsprofiler', 'Offert', 'Bokning', 'Admin', 'Lokal SEO'],
    accent: 'from-sky-500 to-cyan-400',
  },
  {
    name: 'BolagsAnnons',
    category: 'Marknadsplats och säljarpanel',
    url: 'https://bolagsannons.se',
    image: '/project-screenshots/bolagsannons-home.webp',
    description: 'En marknadsplats för företag och digitala verksamheter där säljare kan publicera annonser och köpare kan visa intresse.',
    result: 'Visar arbete med annonser, sökning, formulär, användarflöden, moderering och en separat administrativ vy.',
    features: ['Annonser', 'Sökning', 'Säljarpanel', 'Intresseformulär', 'Admin'],
    accent: 'from-indigo-500 to-sky-400',
  },
];

const androidApps = [
  {
    name: 'BostadBevis',
    status: 'Publicerad på Google Play',
    description: 'En lokal Android-app för att dokumentera en bostads skick med checklistor, foton, anteckningar och rapporter utan krav på konto.',
    result: 'Visar ett komplett mobilflöde med offline-data, kamera, lokal lagring, rapporter och publicering på Google Play.',
    features: ['Offline', 'Kamera', 'Checklistor', 'PDF', 'Google Play'],
    url: 'https://play.google.com/store/apps/details?id=se.codecsverige.bostadbevis',
    image: '/project-screenshots/bostadbevis-play.webp',
  },
  {
    name: 'FakturaVakt',
    status: 'Android-verktyg',
    description: 'En app för att hålla ordning på fakturor, förfallodatum och lokala påminnelser.',
    result: 'Ett exempel på en tydlig vardagsfunktion med lokal data och notifieringar.',
    features: ['Påminnelser', 'Förfallodatum', 'Lokal data'],
  },
  {
    name: 'SnapHandover',
    status: 'Dokumentationsverktyg',
    description: 'Ett offlineverktyg för städning, flytt, fastighetsservice och överlämning med före-, avvikelse- och efterbilder.',
    result: 'Ett exempel på ett arbetsflöde där foton, anteckningar och PDF samlas till en överlämningsrapport.',
    features: ['Offline', 'Foto', 'Anteckningar', 'PDF', 'Flera språk'],
  },
  {
    name: 'International APN Settings',
    status: 'Informationsverktyg',
    description: 'APN-information för mobiloperatörer i flera länder och språk, utformad för snabb åtkomst.',
    result: 'Ett exempel på strukturerad data, sökning och flerspråkigt innehåll i en mobilapp.',
    features: ['Mobilnät', 'Sökning', 'Flera länder', 'Flera språk'],
  },
];

function ProjectVisual({ name, accent = 'from-teal-500 to-cyan-400', mobile = false, image }) {
  if (image) {
    return (
      <div className={`mx-auto overflow-hidden border border-slate-200 bg-white shadow-sm ${mobile ? 'max-w-[520px] rounded-[2rem]' : 'rounded-2xl'}`}>
        <img
          src={image}
          alt={`Skärmbild från ${name}`}
          className={`w-full object-cover object-top ${mobile ? 'aspect-[1200/769]' : 'aspect-[1200/542]'}`}
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div className={`mx-auto overflow-hidden border border-slate-200 bg-white shadow-sm ${mobile ? 'max-w-[220px] rounded-[2rem]' : 'rounded-2xl'}`}>
      <div className={`flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3 ${mobile ? 'justify-center' : ''}`}>
        {mobile ? <span className="h-1.5 w-16 rounded-full bg-slate-300" /> : (
          <>
            <span className="h-2.5 w-2.5 rounded-full bg-rose-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
            <span className="ml-3 h-6 flex-1 rounded-md bg-white" />
          </>
        )}
      </div>
      <div className={`bg-gradient-to-br ${accent} p-6 text-white ${mobile ? 'min-h-[260px]' : ''}`}>
        <div className="mb-8 h-2.5 w-24 rounded-full bg-white/40" />
        <div className="mb-3 h-7 w-3/4 rounded-lg bg-white/90" />
        <div className="mb-2 h-3 w-full rounded-full bg-white/45" />
        <div className="mb-7 h-3 w-4/5 rounded-full bg-white/45" />
        <div className="inline-flex rounded-lg bg-white px-4 py-2 text-sm font-bold text-slate-900">{name}</div>
      </div>
    </div>
  );
}

function ProjectCard({ project, index, mobile = false }) {
  return (
    <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
      <div className="bg-slate-50 p-6 md:p-8">
        <ProjectVisual name={project.name} image={project.image} accent={project.accent || (index % 2 === 0 ? 'from-teal-500 to-cyan-400' : 'from-violet-500 to-indigo-400')} mobile={mobile} />
      </div>
      <div className="p-7 md:p-8">
        <p className="mb-2 font-semibold text-sky-700">{project.category || project.status}</p>
        <h3 className="mb-4 text-2xl font-bold text-slate-950 md:text-3xl">{project.name}</h3>
        <p className="mb-4 leading-relaxed text-slate-600">{project.description}</p>
        <p className="mb-6 rounded-2xl bg-sky-50 p-4 leading-relaxed text-slate-700"><strong className="text-slate-950">Det visar:</strong> {project.result}</p>
        <div className="mb-7 flex flex-wrap gap-2">
          {project.features.map((feature) => <span key={feature} className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-600">{feature}</span>)}
        </div>
        {project.url ? (
          <a href={project.url} target="_blank" rel="noopener noreferrer" className="inline-flex rounded-xl bg-sky-700 px-6 py-3 font-bold text-white transition hover:bg-sky-800">
            {project.name === 'BostadBevis' ? 'Öppna på Google Play ↗' : `Öppna ${project.name} ↗`}
          </a>
        ) : (
          <p className="text-sm text-slate-400">Visas som ett exempel på funktioner och arbetsflöde.</p>
        )}
      </div>
    </article>
  );
}

export default function Portfolio() {
  return (
    <>
      <SEOHead
        title="Projekt med bokning, admin, Firebase och Android | StartaWebb"
        description="Se webb- och Android-projekt med bokning, offertflöden, marknadsplats, admin, Firebase, offline-data, kamera och PDF. Projekten visar vilken typ av avgränsade lösningar StartaWebb kan leverera."
        canonical="https://startawebb.se/portfolio"
        keywords="webbprojekt Sverige, Firebase projekt, bokningssystem småföretag, adminpanel, Android app, Lovable hjälp, slutföra webbprojekt"
      />

      <section className="relative overflow-hidden bg-gradient-to-br from-sky-50 via-white to-cyan-50 py-20">
        <div className="pointer-events-none absolute -right-24 -top-28 h-72 w-72 rounded-full bg-sky-200/40 blur-3xl" />
        <div className="relative mx-auto max-w-5xl px-4 text-center">
          <p className="mb-4 inline-flex rounded-full border border-sky-200 bg-white px-4 py-2 text-sm font-bold text-sky-800">Riktiga flöden, inte bara en startsida</p>
          <h1 className="mb-5 text-4xl font-extrabold tracking-tight text-slate-950 md:text-5xl">Exempel på funktioner som redan är byggda</h1>
          <p className="mx-auto max-w-3xl text-lg leading-relaxed text-slate-600">Egna projekt med formulär, bokning, sökning, administration och mobilverktyg. De visar vad som är byggt och publicerat; de presenteras inte som kunduppdrag.</p>
        </div>
      </section>

      <section className="bg-white px-4 py-8"><div className="mx-auto max-w-6xl rounded-2xl border border-sky-100 bg-sky-50 p-6"><h2 className="text-xl font-bold text-slate-950">Letar du efter en vanlig företagshemsida?</h2><p className="mt-3 text-slate-600">Se ett tydligt upplägg för tjänster, arbetsområden och offertförfrågningar.</p><Link to="/exempel/stadfirma" className="mt-4 inline-flex font-bold text-sky-700 underline">Öppna exemplet för städföretag →</Link></div></section>
      <section className="bg-slate-50 py-20" id="webbprojekt">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-10 max-w-3xl">
            <p className="mb-3 font-semibold text-sky-700">Webbprojekt</p>
            <h2 className="mb-4 text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">Från besökare till ett fungerande arbetsflöde</h2>
            <p className="text-lg leading-relaxed text-slate-600">Exemplen visar hur en webbplats kan göra mer än att presentera text: ta emot förfrågningar, spara data, söka, boka och ge verksamheten en administrativ vy.</p>
          </div>
          <div className="grid gap-7 md:grid-cols-2">
            {webProjects.map((project, index) => <ProjectCard key={project.name} project={project} index={index} />)}
          </div>
        </div>
      </section>

      <section className="bg-white py-20" id="android-appar">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-10 max-w-3xl">
            <p className="mb-3 font-semibold text-teal-700">Android-appar</p>
            <h2 className="mb-4 text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">Mobilverktyg för ett tydligt problem</h2>
            <p className="text-lg leading-relaxed text-slate-600">Apparna är exempel på avgränsade verktyg med lokal data, kamera, påminnelser, rapporter och flera språk.</p>
          </div>
          <div className="grid gap-7 md:grid-cols-2">
            {androidApps.map((project, index) => <ProjectCard key={project.name} project={project} index={index} mobile />)}
          </div>
        </div>
      </section>

      <section className="bg-white pb-20">
        <div className="mx-auto grid max-w-6xl gap-6 px-4 lg:grid-cols-3">
          <article className="rounded-3xl border border-sky-100 bg-sky-50 p-7">
            <p className="mb-3 font-semibold text-sky-700">Ny eller gammal hemsida</p>
            <h2 className="mb-4 text-2xl font-bold text-slate-950">Gör det lättare att förstå och ta kontakt</h2>
            <p className="mb-5 leading-relaxed text-slate-600">En enkel ny företagshemsida har cirka 4 900 kr som riktpris. Slutpris och eventuell moms framgår i offerten.</p>
            <Link to="/contact?behov=hemsida" className="font-bold text-sky-700 hover:text-sky-900">Beskriv hemsidan →</Link>
          </article>
          <article className="rounded-3xl border border-violet-100 bg-violet-50 p-7">
            <p className="mb-3 font-semibold text-violet-700">Projekt som har fastnat</p>
            <h2 className="mb-4 text-2xl font-bold text-slate-950">Slutför ett Lovable-, Cursor- eller AI-projekt</h2>
            <p className="mb-5 leading-relaxed text-slate-600">Skicka länken och det viktigaste som inte fungerar. Projektet bedöms innan en avgränsad leverans och ett pris lämnas.</p>
            <Link to="/ai-projekt" className="font-bold text-violet-700 hover:text-violet-900">Se AI-projekthjälpen →</Link>
          </article>
          <article className="rounded-3xl border border-teal-100 bg-teal-50 p-7">
            <p className="mb-3 font-semibold text-teal-700">Mindre digitalt verktyg</p>
            <h2 className="mb-4 text-2xl font-bold text-slate-950">Bokning, admin, foto eller rapporter</h2>
            <p className="mb-5 leading-relaxed text-slate-600">Beskriv det manuella arbetsflödet och vad som ska bli enklare. Funktionerna avgör omfattning och pris.</p>
            <Link to="/contact?behov=verktyg" className="font-bold text-teal-700 hover:text-teal-900">Beskriv arbetsflödet →</Link>
          </article>
        </div>
      </section>
    </>
  );
}
